import { createFileRoute } from "@tanstack/react-router";
import { Archive, Pencil, Plus, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { EmptyState } from "@/components/brand/empty-state";
import { ErrorState } from "@/components/brand/error-state";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useCreateFramework, useFrameworks, useUpdateFramework } from "@/features/prompts";
import { ActivityTypeManager } from "@/features/tracker";
import { ApiError, type FrameworkPublic } from "@/lib/api";

/**
 * The shapes a prompt can take, owned by The Motherhood.
 *
 * Global rather than per-client on purpose: "fill-in-the-blank" is a way of
 * writing, not a client trait, and one shared set is what lets prompts be
 * compared across clients later.
 */
export const Route = createFileRoute("/_authenticated/admin/frameworks")({
  component: FrameworksPage,
});

function FrameworksPage() {
  const { data, isPending, isError, refetch } = useFrameworks(true);
  const [editing, setEditing] = useState<FrameworkPublic | null>(null);
  const [adding, setAdding] = useState(false);
  const update = useUpdateFramework();

  return (
    <>
      <PageHeader
        eyebrow="The Motherhood"
        title="Prompt frameworks"
        description="The shapes a prompt can take. Every prompt, for every client, picks one of these. They are shared across all clients, so keep them general."
        actions={
          <Button onClick={() => setAdding(true)}>
            <Plus /> New framework
          </Button>
        }
      />

      {isPending ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : data.length === 0 ? (
        <EmptyState
          title="No frameworks yet"
          description="Add the shapes you teach, for example fill-in-the-blank."
          action={
            <Button onClick={() => setAdding(true)}>
              <Plus /> Add the first one
            </Button>
          }
        />
      ) : (
        <ul className="space-y-3">
          {data.map((framework) => (
            <li
              key={framework.id}
              className="rounded-lg border border-border bg-card p-4 shadow-soft"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-semibold text-foreground">{framework.name}</h2>
                    {framework.isActive ? null : <Badge variant="outline">Retired</Badge>}
                    <Badge>
                      {framework.promptCount} prompt{framework.promptCount === 1 ? "" : "s"}
                    </Badge>
                  </div>
                  {framework.description ? (
                    <p className="mt-1 text-muted-foreground text-sm">{framework.description}</p>
                  ) : null}
                  {framework.example ? (
                    <p className="mt-2 rounded-md bg-muted/50 px-3 py-2 text-sm italic">
                      {framework.example}
                    </p>
                  ) : null}
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Edit ${framework.name}`}
                    onClick={() => setEditing(framework)}
                  >
                    <Pencil /> Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={update.isPending}
                    aria-label={
                      framework.isActive
                        ? `Retire ${framework.name}`
                        : `Bring back ${framework.name}`
                    }
                    onClick={() =>
                      update.mutate({
                        path: { framework_id: framework.id },
                        body: { isActive: !framework.isActive },
                      })
                    }
                  >
                    {framework.isActive ? (
                      <>
                        <Archive /> Retire
                      </>
                    ) : (
                      <>
                        <RotateCcw /> Bring back
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <FrameworkDialog
        framework={editing}
        open={adding || editing !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAdding(false);
            setEditing(null);
          }
        }}
      />

      <ActivityTypeManager />
    </>
  );
}

function FrameworkDialog({
  framework,
  open,
  onOpenChange,
}: {
  framework: FrameworkPublic | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = useCreateFramework();
  const update = useUpdateFramework();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [example, setExample] = useState("");

  useEffect(() => {
    if (!open) return;
    setName(framework?.name ?? "");
    setDescription(framework?.description ?? "");
    setExample(framework?.example ?? "");
  }, [open, framework]);

  const pending = create.isPending || update.isPending;
  const error = [create.error, update.error].find(Boolean);
  const message =
    error instanceof ApiError
      ? error.code === "name_taken"
        ? "A framework with that name already exists."
        : error.message
      : error
        ? "Could not save. Please try again."
        : null;

  const submit = () => {
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      example: example.trim() || null,
    };
    if (framework) {
      update.mutate(
        { path: { framework_id: framework.id }, body },
        { onSuccess: () => onOpenChange(false) },
      );
      return;
    }
    create.mutate({ body }, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{framework ? "Edit framework" : "New framework"}</DialogTitle>
          <DialogDescription>
            A way of writing a prompt. Every client sees this, so describe the shape rather than any
            one organisation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="framework-name">Name</Label>
            <Input
              id="framework-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Fill-in-the-blank"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="framework-description">What it is</Label>
            <Textarea
              id="framework-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A sentence with gaps the writer completes from their own week."
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="framework-example">Example</Label>
            <p className="text-muted-foreground text-xs">
              Shown under the dropdown when someone writes a prompt, as a model of the shape.
            </p>
            <Textarea
              id="framework-example"
              rows={2}
              value={example}
              onChange={(e) => setExample(e.target.value)}
              placeholder="I recently learned ___ while working on ___."
            />
          </div>
          {message ? (
            <p role="alert" className="text-destructive-strong text-sm">
              {message}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={name.trim().length === 0 || pending}>
            {framework ? "Save changes" : "Add framework"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
